import { useState } from "react";
import { ActionModal, Button } from "@cntrl/ui";
import { useCapability } from "../../app/bandContext.js";
import { useMe } from "../../app/hooks/useSession.js";
import { errorMessage } from "../../app/hooks/apiError.js";
import { useShowMembers, useRespondMember } from "../../app/hooks/useShows.js";
import { useRespondShare, type SharedShowDetail } from "../../app/hooks/useShowShares.js";
import styles from "./ShowBackOut.module.scss";

/**
 * "Back out" is TWO acts with two blast radii (ART-229), and those two are two different SUBJECTS — "the band"
 * and "you". The roster already draws those subjects as two rows, so each act rides the row that IS its
 * subject instead of a footer button that has to ASK which one you mean:
 *
 *   • {@link BandBackOut}   — the trailing action of the LINEUP row. Declines the whole band's SHARE: every
 *     member comes off the bill, and the host must re-invite to rejoin (terminal). Guest show only, and only
 *     once the band has ACCEPTED — an unanswered invite is declined from the Booking "Invites" group, not here.
 *   • {@link MemberBackOut} — the trailing action of the MEMBERS row. Passes on YOUR OWN `show_members` RSVP:
 *     you come off the crew, the show goes on, and it is reversible. Works on a guest show AND on your own
 *     band's show.
 *
 * The AV-member-on-crew case (the one the old intermediate chooser existed for) now simply shows BOTH buttons,
 * one per row — the location is the answer, so there is no chooser. Each self-gates and renders nothing when
 * its act does not apply, so "the owner sees nothing" and "a read-only member sees nothing" fall out of the
 * two predicates rather than needing a branch.
 */

export interface BandBackOutProps {
  /** The host show id. */
  showId: string;
  /** The guest-context matrix — carries the `shareId` to decline and the share `status`. Present only in guest
   *  mode (an own-band show has no share to back out of). */
  guest: SharedShowDetail;
  /** The mounted (acting/guest) band id — resolves the band's own name for the confirm copy. */
  bandId: string;
  /** Called after a successful decline — the caller closes the show view. */
  onDone: () => void;
  /** Surface the decline error verbatim in the caller's footer, rather than a toast (this is a terminal,
   *  high-stakes act, so the exact server reason — e.g. a friendship 409 — is worth showing, not summarising). */
  onError: (message: string | null) => void;
}

/** The LINEUP row's "back out" — declines the whole band's share (AV / `schedule:write`, accepted share only). */
export function BandBackOut({ showId, guest, bandId, onDone, onError }: BandBackOutProps) {
  const me = useMe();
  const canWrite = useCapability("schedule:write");
  const declineShare = useRespondShare(showId);
  const [confirming, setConfirming] = useState(false);

  const mySeat = me.data?.seats?.find((s) => s.bandId === bandId);
  const bandName = mySeat?.displayName?.trim() || "your band";
  // Declining the whole share is a `schedule:write` act and exists only once the band has ACCEPTED — a still
  // pending invite is answered (accept/deny) from the Booking "Invites" group, never "backed out of".
  const canBand = canWrite && guest.status === "accepted";
  if (!canBand) return null;

  return (
    <>
      <Button
        variant="ghost"
        tone="danger"
        size="xs"
        className={styles.backOut}
        loading={declineShare.isPending}
        // The visible label is the row-scoped short form; the accessible name carries the whole meaning, and
        // stays distinct from the member row's "Back out" so the two are never one ambiguous control.
        aria-label={`Back ${bandName} out of this show`}
        onClick={() => setConfirming(true)}
      >
        Back out
      </Button>

      {confirming && (
        <ActionModal
          title={`Back ${bandName} out?`}
          confirmLabel="Yes, back out"
          confirmTone="danger"
          loading={declineShare.isPending}
          onConfirm={() => {
            setConfirming(false);
            onError(null);
            declineShare.mutate(
              { shareId: guest.shareId as string, accept: false },
              {
                onSuccess: onDone,
                onError: (e) => onError(errorMessage(e, "Couldn't back out, please refresh and try again.")),
              },
            );
          }}
          onCancel={() => setConfirming(false)}
        >
          <p className={styles.confirmText}>
            Are you sure you want to back {bandName} out from this show completely? All {bandName} members will be
            backed out, and you&apos;ll need to be re-invited by the host to rejoin.
          </p>
        </ActionModal>
      )}
    </>
  );
}

export interface MemberBackOutProps {
  /** The show id. */
  showId: string;
  /** The mounted band id — resolves the caller's own seat (owner vs crew member). */
  bandId: string;
  /** Called after a successful pass — the caller closes the show view. */
  onDone: () => void;
}

/** The MEMBERS row's "back out" — passes on the caller's own crew RSVP (any non-owner member who is still in). */
export function MemberBackOut({ showId, bandId, onDone }: MemberBackOutProps) {
  const me = useMe();
  const members = useShowMembers(showId);
  const respondMember = useRespondMember(showId);
  const [confirming, setConfirming] = useState(false);

  const myPersonId = me.data?.person?.personId;
  const mySeat = me.data?.seats?.find((s) => s.bandId === bandId);
  const isOwner = mySeat?.isOwner === true;
  const myMember = members.data?.items?.find((m) => m.personId === myPersonId);
  // Back out is for a member who has ACCEPTED (rsvp "in") — you pass on a spot you took. The ARTIST/owner
  // account IS the band, never a crew member, so it has no personal spot (its only act is the band decline).
  // A PENDING member answers the invite (Accept/Decline) from the dashboard's invites, NOT here — the same
  // split the band side makes, where a pending share is answered in the Invites group and BandBackOut is
  // offered only once accepted. And an 'out' member has already passed.
  const canSelf = !isOwner && myMember?.rsvp === "in";
  if (!canSelf) return null;

  return (
    <>
      <Button
        variant="ghost"
        tone="danger"
        size="xs"
        className={styles.backOut}
        loading={respondMember.isPending}
        aria-label="Back out of your own spot on this show"
        onClick={() => setConfirming(true)}
      >
        Back out
      </Button>

      {confirming && (
        <ActionModal
          title="Back out as a member?"
          confirmLabel="Yes, back out"
          confirmTone="danger"
          loading={respondMember.isPending}
          onConfirm={() => {
            setConfirming(false);
            // useRespondMember toasts its own error; success closes the view.
            respondMember.mutate(
              { memberId: myMember!.showMemberId as string, response: "out" },
              { onSuccess: onDone },
            );
          }}
          onCancel={() => setConfirming(false)}
        >
          <p className={styles.confirmText}>
            Are you sure you want to back out from being a member in this show? You&apos;ll come off the crew for
            this date — the show goes on without you, and you can rejoin.
          </p>
        </ActionModal>
      )}
    </>
  );
}
